/**
 * AITab.js — AI Intelligence Dashboard
 *
 * Features:
 *  1. 🔴 Live Ticket Monitor  — polls every 30s, rush detection, stale alerts, open count
 *  2. 🧠 AI Insights Briefing — calls /api/ai-insights (OpenAI or rule-based)
 *  3. 📊 Anomaly Feed         — real-time statistical anomaly alerts with severity badges
 *  4. 🔔 Browser Notifications — permission request + push on critical events
 *  5. ⚙️ Smart Thresholds     — configurable ticket count / staleness / revenue pace
 *
 * Data sources:
 *  - /api/square-tickets       (live orders, polled every 30s)
 *  - /api/square-sales         (today's running totals, polled every 2 min)
 *  - /api/ai-insights          (AI analysis, refreshed on demand)
 *  - anomalyEngine.js          (Z-score / velocity / stale detection)
 *  - Open-Meteo                (current weather, used in AI context)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fmt$ } from '../../../utils/formatUtils';
import {
  API_BASE, decodeWMO,
  fetchLiveTickets, fetchTodaySales, fetchHistoricalSales, fetchCurrentWeather,
} from '../squareDashboardUtils';
import NotificationStatusBar from './ai/NotificationStatusBar';
import LiveTicketHeader from './ai/LiveTicketHeader';
import AnomalyFeed from './ai/AnomalyFeed';
import AIBriefingPanel from './ai/AIBriefingPanel';
import OpenTicketsQueue from './ai/OpenTicketsQueue';
import AlertHistoryLog from './ai/AlertHistoryLog';
import ThresholdSettings from './ai/ThresholdSettings';
import {
  collection, query, where, onSnapshot, orderBy, limit,
  doc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  runAllAnomalyDetectors,
  computeSalesStats,
  detectTicketVelocity,
} from '../../../utils/anomalyEngine';
import {
  initFCM,
  sendServerAlert,
  onForegroundMessage,
  getCachedFCMToken,
} from '../../../utils/fcmUtils';

// ─── Constants ────────────────────────────────────────────────────────────────
const TICKET_POLL_MS  = 30_000;   // 30 seconds
const SALES_POLL_MS   = 120_000;  // 2 minutes
const AI_REFRESH_MS   = 300_000;  // 5 minutes
const DEFAULT_THRESHOLDS = {
  openTicketsAlert:    7,    // notify when 7+ tickets open at the same time
  openIn20MinAlert:    7,    // notify when 7+ tickets opened within last 20 min
  staleMinutes:       20,   // tickets open longer than this = stale
  rushMultiplier:     2.0,  // current rate / baseline > this = rush
  revPaceLow:         0.6,  // projected EOD < 60% of expected = alert
  revPaceHigh:        1.4,  // projected EOD > 140% = alert
};

// ─── Browser Notification Helpers (foreground fallback) ─────────────────────
function pushBrowserNotification(title, body) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag:  title,
      requireInteraction: false,
    });
  } catch (e) { /* ignore */ }
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function AITab({ onOpenCountChange }) {
  // State
  const [liveTickets,     setLiveTickets]     = useState(null);
  const [todaySales,      setTodaySales]      = useState(null);
  const [salesHistory,    setSalesHistory]    = useState({});
  const [weather,         setWeather]         = useState(null);
  const [aiResult,        setAiResult]        = useState(null);
  const [anomalyReport,   setAnomalyReport]   = useState(null);
  const [alertHistory,    setAlertHistory]    = useState([]); // persisted alert log
  const [thresholds,      setThresholds]      = useState(DEFAULT_THRESHOLDS);
  const [notifPerm,       setNotifPerm]       = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [fcmReady,        setFcmReady]        = useState(false);   // FCM token obtained
  const [fcmError,        setFcmError]        = useState(null);    // reason if FCM failed
  const [smsEnabled,      setSmsEnabled]      = useState(true);    // SMS channel toggle
  const [helpRequests,    setHelpRequests]    = useState([]);      // live Firestore help requests
  const [histLoading,     setHistLoading]     = useState(true);
  const [aiLoading,       setAiLoading]       = useState(false);
  const [lastAiRefresh,   setLastAiRefresh]   = useState(null);
  const [lastTicketPoll,  setLastTicketPoll]  = useState(null);
  const [ticketEventLog,  setTicketEventLog]  = useState([]); // [{timestamp, id}]
  const [prevOpenIds,     setPrevOpenIds]     = useState(new Set());

  // Track start-of-operating-day for elapsed minutes
  const operatingStartRef = useRef(null);
  if (!operatingStartRef.current) {
    const d = new Date();
    d.setHours(10, 0, 0, 0); // assume 10am open
    operatingStartRef.current = d.getTime();
  }

  const elapsedMinutes = useMemo(() => {
    const now = Date.now();
    return Math.max(0, (now - operatingStartRef.current) / 60000);
  }, [lastTicketPoll]); // re-compute each poll cycle

  // ── FCM + Notification init ────────────────────────────────────────────
  const requestPermission = useCallback(async () => {
    const { token, error } = await initFCM();
    if (token) {
      setFcmReady(true);
      setFcmError(null);
      setNotifPerm('granted');
    } else {
      setFcmError(error);
      // Fallback: try plain browser notification
      if (typeof Notification !== 'undefined') {
        const p = await Notification.requestPermission();
        setNotifPerm(p);
      }
    }
  }, []);

  // Init FCM on mount (silent — only registers if permission already granted)
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      initFCM().then(({ token, error }) => {
        if (token) { setFcmReady(true); setNotifPerm('granted'); }
        if (error)  setFcmError(error);
      });
    }
    // Listen for foreground FCM messages (show in-tab toast)
    const unsub = onForegroundMessage(payload => {
      const { title = 'Alert', body = '' } = payload?.notification || {};
      setAlertHistory(prev => [{
        id: 'fcm_' + Date.now(), title, body,
        severity: payload?.data?.severity || 'high',
        timestamp: Date.now(),
        channel: 'fcm_foreground',
      }, ...prev].slice(0, 50));
      pushBrowserNotification(title, body);
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Previously seen alert IDs to avoid duplicates ─────────────────────────
  const seenAlertsRef = useRef(new Set());

  // ── Send a smart notification (server SMS + FCM + browser + in-app log) ────────
  const sendAlert = useCallback((id, title, body, severity = 'medium') => {
    if (seenAlertsRef.current.has(id)) return; // debounce
    seenAlertsRef.current.add(id);
    // Auto-expire dedupe after 5 minutes
    setTimeout(() => seenAlertsRef.current.delete(id), 5 * 60 * 1000);

    // Always log in-app
    setAlertHistory(prev => [{
      id, title, body, severity,
      timestamp: Date.now(),
      channel: 'pending',
    }, ...prev].slice(0, 50));

    if (severity === 'critical' || severity === 'high') {
      // ── Server-side: SMS to owner phone + FCM push to all registered devices
      const channels = [];
      if (smsEnabled)  channels.push('sms');
      if (fcmReady || getCachedFCMToken()) channels.push('fcm');
      if (channels.length > 0) {
        sendServerAlert({ alertKey: id, title, body, severity, channels })
          .then(result => {
            // Update log entry with channel info
            setAlertHistory(prev => prev.map(a =>
              a.id === id
                ? { ...a, channel: result?.results?.sms?.ok ? 'sms+fcm' : result?.results?.fcm?.ok ? 'fcm' : 'failed' }
                : a
            ));
          });
      }

      // Also fire in-tab browser notification (foreground fallback)
      pushBrowserNotification(`Desi Bites — ${title}`, body);
    }
  }, [smsEnabled, fcmReady]);

  // ── Poll live tickets ─────────────────────────────────────────────────────
  const pollTickets = useCallback(async () => {
    try {
      const data = await fetchLiveTickets();
      const orders = data.orders || [];
      const openOrders = orders.filter(o => o.state === 'OPEN');

      // Track new ticket opens for velocity
      const openIds = new Set(openOrders.map(o => o.id));
      const newIds  = [...openIds].filter(id => !prevOpenIds.has(id));
      if (newIds.length > 0) {
        const now = Date.now();
        setTicketEventLog(prev => [
          ...newIds.map(id => ({ id, timestamp: now })),
          ...prev,
        ].filter(e => now - e.timestamp < 60 * 60 * 1000)); // keep 1 hr window
      }
      setPrevOpenIds(openIds);

      // Compute stale tickets (use createdAt from ticket data)
      const stale = openOrders
        .filter(o => {
          const opened = o.createdAt ? new Date(o.createdAt).getTime() : 0;
          return opened && (Date.now() - opened) >= thresholds.staleMinutes * 60 * 1000;
        })
        .map(o => ({
          id:       o.id,
          name:     o.ticketName || o.shortId || o.id,
          openedAt: new Date(o.createdAt).getTime(),
          minutesOpen: Math.round((Date.now() - new Date(o.createdAt).getTime()) / 60000),
          total:    o.total,
        }))
        .sort((a, b) => b.minutesOpen - a.minutesOpen);

      setLiveTickets({ orders, openOrders, stale, fetchedAt: data.fetchedAt });
      setLastTicketPoll(Date.now());

      // ── Trigger alerts based on current state ──
      // Rush alert
      const vel = detectTicketVelocity(
        [...(ticketEventLog || []), ...newIds.map(id => ({ id, timestamp: Date.now() }))],
        Date.now()
      );
      if (vel.rushDetected) {
        sendAlert('rush_' + Math.floor(Date.now() / 300000), // 5-min bucket
          '🔥 Rush Alert',
          `${vel.recentCount} new tickets in 10 min (${vel.multiplier.toFixed(1)}x normal rate). All stations active!`,
          'critical');
      }

      // ── Open tickets threshold (simultaneous open)
      if (openOrders.length >= thresholds.openTicketsAlert) {
        sendAlert('open_thresh_' + Math.floor(Date.now() / 60000),
          `🎫 ${openOrders.length} Tickets Open`,
          `Open ticket count (${openOrders.length}) hit your alert threshold (${thresholds.openTicketsAlert}). Staff up!`,
          'high');
      }

      // ── 20-min window: tickets OPENED in last 20 minutes
      const windowMs  = 20 * 60 * 1000;
      const cutoff20  = Date.now() - windowMs;
      const recentLog = [
        ...newIds.map(id => ({ id, timestamp: Date.now() })),
        ...(ticketEventLog || []),
      ].filter(e => e.timestamp >= cutoff20);
      const uniqueIn20 = new Set(recentLog.map(e => e.id)).size;
      if (uniqueIn20 >= thresholds.openIn20MinAlert) {
        sendAlert(
          `open20_${Math.floor(Date.now() / (5 * 60 * 1000))}`, // 5-min bucket dedup
          `⚡ ${uniqueIn20} Tickets in Last 20 Min`,
          `${uniqueIn20} tickets opened in the last 20 minutes — we’re in a rush! All stations active.`,
          'critical'
        );
      }

      // Automatic notifications removed — employees use the "Need Help" button on tickets.
      // Velocity and stale info is shown in the live dashboard panels below.
    } catch (e) {
      console.warn('Ticket poll failed:', e.message);
    }
  }, [thresholds, prevOpenIds, ticketEventLog]);

  // ── Poll today's sales ────────────────────────────────────────────────────
  const pollSales = useCallback(async () => {
    try {
      const data = await fetchTodaySales();
      if (data.ok) setTodaySales(data);
    } catch (e) {
      console.warn('Sales poll failed:', e.message);
    }
  }, []);

  // ── Load historical sales (once on mount) ─────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const [hist, wx] = await Promise.all([
        fetchHistoricalSales(60),
        fetchCurrentWeather(),
      ]);
      setSalesHistory(hist);
      if (wx) setWeather({ ...wx, ...decodeWMO(wx.wmo) });
    } catch (e) {
      console.warn('History load failed:', e.message);
    } finally {
      setHistLoading(false);
    }
  }, []);

  // Report live open count to parent (for badge)
  useEffect(() => {
    if (onOpenCountChange) onOpenCountChange(liveTickets?.openOrders?.length || 0);
  }, [liveTickets, onOpenCountChange]);

  // ── Run anomaly detectors ─────────────────────────────────────────────────
  useEffect(() => {
    if (!todaySales) return;
    const revenue  = parseFloat(todaySales?.summary?.totalRevenue || 0);
    const tickets  = parseInt(todaySales?.summary?.totalTickets   || 0, 10);
    const openOrds = liveTickets?.openOrders || [];
    const report   = runAllAnomalyDetectors({
      todayRevenue:   revenue,
      todayTickets:   tickets,
      elapsedMinutes,
      salesHistory,
      openTickets:    openOrds.map(o => ({
        id:       o.id,
        openedAt: o.createdAt ? new Date(o.createdAt).getTime() : Date.now(),
      })),
      ticketEventLog,
    });
    setAnomalyReport(report);

    // Fire alerts for detected revenue anomalies
    report.anomalies.forEach(a => {
      if (a.severity === 'critical' || a.severity === 'high') {
        sendAlert(a.id, a.title, a.message, a.severity);
      }
    });
  }, [todaySales, liveTickets, salesHistory, elapsedMinutes, ticketEventLog, sendAlert]);

  // ── Fetch AI insights ────────────────────────────────────────────────────
  const refreshAI = useCallback(async () => {
    if (aiLoading) return;
    setAiLoading(true);
    try {
      const revenue    = parseFloat(todaySales?.summary?.totalRevenue || 0);
      const tickets    = parseInt(todaySales?.summary?.totalTickets   || 0, 10);
      const avgTicket  = tickets > 0 ? revenue / tickets : 0;
      const stats      = computeSalesStats(revenue, tickets, salesHistory, elapsedMinutes);
      const openOrds   = liveTickets?.openOrders || [];
      const stale      = liveTickets?.stale      || [];

      // Build top items from today's sales categories
      const topItems = (todaySales?.categories || [])
        .flatMap(c => c.items || [])
        .map(it => ({ name: it.name, qty: it.qty || 0, revenue: it.revenue || 0 }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      const body = {
        salesSummary: {
          todayRevenue:  revenue,
          todayTickets:  tickets,
          todayAvgTicket: avgTicket,
          expectedEOD:   stats.expectedEOD,
        },
        anomalies:         (anomalyReport?.anomalies || []).map(a => ({ severity: a.severity, message: a.message, title: a.title })),
        ticketStats: {
          openCount:    openOrds.length,
          recentCount:  anomalyReport?.velocity?.recentCount || 0,
          avgWaitMin:   stale.length > 0 ? stale[0].minutesOpen : 0,
          staleCount:   stale.length,
          rushAlert:    anomalyReport?.rushDetected || false,
        },
        weather: weather ? {
          current: { label: weather.label, temp: weather.temp, wind: weather.wind, precip: weather.precip, cat: weather.cat },
          forecastImpact: weather.cat === 'rainy' ? 'negative' : weather.cat === 'clear' ? 'positive' : 'moderate',
        } : {},
        topItems,
        periodComparisons: {
          vsYesterday:      stats.vsYesterday,
          vsLastWeekSameDay: stats.vsLastWeekSameDay,
          dayName:          stats.dayName,
        },
      };

      const res = await fetch(`${API_BASE}/api/ai-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`AI API ${res.status}`);
      const data = await res.json();
      if (data.ok) {
        setAiResult(data);
        setLastAiRefresh(Date.now());
      }
    } catch (e) {
      console.warn('AI refresh failed:', e.message);
    } finally {
      setAiLoading(false);
    }
  }, [todaySales, salesHistory, liveTickets, weather, anomalyReport, elapsedMinutes, aiLoading]);

  // ── Polling intervals ─────────────────────────────────────────────────────
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  // ── Live Firestore help requests (real-time, no polling needed) ────────────
  useEffect(() => {
    const q = query(
      collection(db, 'helpRequests'),
      where('status', '==', 'open'),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
    const unsub = onSnapshot(q, snap => {
      const requests = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || null,
      }));
      setHelpRequests(requests);
      // Fire browser notification + in-app log for each new request
      requests.forEach(r => {
        const alertId = `help_${r.id}`;
        if (!seenAlertsRef.current.has(alertId)) {
          seenAlertsRef.current.add(alertId);
          pushBrowserNotification(
            `🏞️ ${r.employeeName || 'Staff'} Needs Help!`,
            `Ticket: ${r.ticketName}${r.message ? ' — ' + r.message : ''}`,
          );
          setAlertHistory(prev => [{
            id: alertId,
            title: `🏞️ ${r.employeeName || 'Staff'} Needs Help`,
            body: `Ticket: ${r.ticketName}${r.message ? ' — ' + r.message : ''}`,
            severity: 'critical',
            timestamp: r.createdAt ? r.createdAt.getTime() : Date.now(),
            channel: 'sms+fcm',
          }, ...prev].slice(0, 50));
        }
      });
    }, e => console.warn('[Help] Firestore listener error:', e.message));
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acknowledgeHelp = useCallback(async (helpId) => {
    await setDoc(doc(db, 'helpRequests', helpId), {
      status: 'resolved',
      resolvedAt: serverTimestamp(),
    }, { merge: true });
  }, []);
  useEffect(() => {
    pollTickets();
    pollSales();
    const ticketTimer = setInterval(pollTickets, TICKET_POLL_MS);
    const salesTimer  = setInterval(pollSales,   SALES_POLL_MS);
    return () => { clearInterval(ticketTimer); clearInterval(salesTimer); };
  }, [pollTickets, pollSales]);

  // Auto-refresh AI when data changes (throttled)
  useEffect(() => {
    if (!todaySales || histLoading) return;
    const now = Date.now();
    if (!lastAiRefresh || (now - lastAiRefresh) >= AI_REFRESH_MS) {
      refreshAI();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todaySales?.summary?.totalRevenue, liveTickets?.openOrders?.length]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const revenue     = parseFloat(todaySales?.summary?.totalRevenue || 0);
  const ticketCount = parseInt(todaySales?.summary?.totalTickets   || 0, 10);
  const openCount   = liveTickets?.openOrders?.length || 0;
  const staleCount  = liveTickets?.stale?.length      || 0;
  const rushActive  = anomalyReport?.rushDetected     || false;
  const allAlerts   = (anomalyReport?.anomalies || []).filter(a => a.severity === 'critical' || a.severity === 'high');

  return (
    <div className="ai__root">
      <NotificationStatusBar
        notifPerm={notifPerm} fcmReady={fcmReady} fcmError={fcmError}
        smsEnabled={smsEnabled} setSmsEnabled={setSmsEnabled}
        requestPermission={requestPermission} sendAlert={sendAlert}
      />

      <LiveTicketHeader
        openCount={openCount} staleCount={staleCount} staleMinutes={thresholds.staleMinutes}
        ticketCount={ticketCount} revenue={revenue} rushActive={rushActive}
        alertThreshold={thresholds.openTicketsAlert} lastTicketPoll={lastTicketPoll}
      />

      {/* Stale tickets */}
      {staleCount > 0 && (
        <div className="ai__section ai__section--warn">
          <div className="ai__section-title">⏱️ Stale Open Tickets — Action Required</div>
          <div className="ai__stale-grid">
            {(liveTickets?.stale || []).map(t => (
              <div key={t.id} className={`ai__stale-card ${t.minutesOpen >= 30 ? 'ai__stale-card--critical' : ''}`}>
                <div className="ai__stale-time">{t.minutesOpen} min</div>
                <div className="ai__stale-name">{t.name}</div>
                <div className="ai__stale-amt">{t.total ? fmt$(t.total) : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AnomalyFeed alerts={allAlerts} />

      <AIBriefingPanel
        aiResult={aiResult} aiLoading={aiLoading}
        lastAiRefresh={lastAiRefresh} refreshAI={refreshAI}
      />

      <OpenTicketsQueue
        openOrders={liveTickets?.openOrders}
        staleMinutes={thresholds.staleMinutes}
      />

      <AlertHistoryLog alertHistory={alertHistory} />

      <ThresholdSettings thresholds={thresholds} setThresholds={setThresholds} />

      <div className="ai__footer">
        <span>📲 Alerts: SMS → +19376540871 {smsEnabled ? '✅' : '❌'} &nbsp;·&nbsp; FCM Push {fcmReady ? '✅' : '⚠️'}</span>
        <span>&nbsp;·&nbsp; Tickets polled every 30s · Sales polled every 2m · AI briefing every 5m</span>
      </div>
    </div>
  );
}
